import React, { useState, useEffect } from 'react';
import './About.css';
import { useLocation } from 'react-router-dom';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Simple mount animation trigger
    setIsVisible(true);
  }, []);

  const location = useLocation();
  const isAboutRoute = location?.pathname === '/about';
  const features = [
    {
      icon: '🏆',
      title: 'Premium Quality',
      description: 'All our instruments are manufactured using the highest grade stainless steel and undergo rigorous quality testing.'
    },
    {
      icon: '🔬',
      title: 'Advanced Technology',
      description: 'We utilize cutting-edge manufacturing processes and precision engineering for superior instrument performance.'
    },
    {
      icon: '⚕️',
      title: 'Medical Certified',
      description: 'Our products meet international medical standards and are certified by leading healthcare regulatory bodies.'
    },
    {
      icon: '🌍',
      title: 'Global Reach',
      description: 'Serving healthcare professionals worldwide with reliable delivery and comprehensive customer support.'
    },
    {
      icon: '🔧',
      title: 'Custom Solutions',
      description: 'We offer customized surgical instruments tailored to specific medical procedures and requirements.'
    },
    {
      icon: '📞',
      title: '24/7 Support',
      description: 'Round-the-clock customer service and technical support to ensure uninterrupted medical operations.'
    }
  ];

  // Additional About data
  const values = [
    { icon: '🧩', title: 'Integrity', text: 'We operate with transparency, accountability, and respect in every relationship.' },
    { icon: '✨', title: 'Innovation', text: 'Continuous improvement through R&D and precision engineering.' },
    { icon: '🛡️', title: 'Quality First', text: 'Quality controls embedded at every step—materials, machining, finishing, and inspection.' },
    { icon: '🤝', title: 'Customer-Centric', text: 'Solutions tailored to clinical needs, backed by responsive support.' },
    { icon: '🌱', title: 'Sustainability', text: 'Responsible sourcing and durable products designed to last.' },
    { icon: '📋', title: 'Compliance', text: 'Processes aligned to global medical device standards and good manufacturing practices.' }
  ];

  const processSteps = [
    { step: '01', title: 'Design & Engineering', text: 'Collaborating with clinicians to translate requirements into robust instrument designs.' },
    { step: '02', title: 'Material Selection', text: 'High‑grade stainless steel and alloys selected for durability and performance.' },
    { step: '03', title: 'Precision Manufacturing', text: 'CNC machining, forging, and fine finishing ensure reliable, consistent output.' },
    { step: '04', title: 'Quality Assurance', text: 'Dimensional checks, functional tests, and surface integrity inspections.' },
    { step: '05', title: 'Packaging & Sterility', text: 'Packaging tailored to sterile processing and hospital logistics.' },
    { step: '06', title: 'Delivery & Support', text: 'On‑time delivery and post‑sales guidance for clinical teams.' }
  ];

  const timelineItems = [
    { year: 'Foundation', event: 'Oxgen Surgicals established to serve clinicians with reliable instruments.' },
    { year: 'Global Reach', event: 'Expanded distribution and partnerships to support hospitals internationally.' },
    { year: 'Process Excellence', event: 'Scaled precision manufacturing and embedded quality assurance systems.' },
    { year: 'Customer Solutions', event: 'Launched custom sets tailored for specialized procedures.' },
    { year: 'Continuous R&D', event: 'Ongoing product refinement driven by practitioner feedback.' }
  ];

  const certifications = [
    { badge: 'Quality Systems', note: 'Operations aligned with recognized quality management practices for medical devices.' },
    { badge: 'Material Compliance', note: 'Premium material sourcing with traceability and batch controls.' },
    { badge: 'Process Controls', note: 'Documented procedures across manufacturing and inspection.' },
    { badge: 'Customer Assurance', note: 'Commitment to transparent documentation and test reports upon request.' }
  ];

  const team = [
    { initials: 'OF', role: 'Founder & CEO', text: 'Leads strategy and customer relationships with a focus on quality and trust.' },
    { initials: 'HP', role: 'Head of Production', text: 'Oversees manufacturing excellence and process optimization.' },
    { initials: 'QL', role: 'Quality Lead', text: 'Ensures conformance through rigorous inspection and testing.' },
    { initials: 'SD', role: 'Sales Director', text: 'Builds partnerships and supports clinical teams worldwide.' }
  ];

  return (
    <section id="about" className="about section">
      <div className="about-cover" style={{ backgroundImage: "url('/our.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <div className={`section-header ${isVisible ? 'animate-in' : ''}`}>
            <h2 className="section-title">About Oxgen Surgicals</h2>
            <p className="section-description">
              Leading the industry in surgical instrument manufacturing with precision, quality, and innovation
            </p>
          </div>

          <div className="about-content">
            <div className={`about-text ${isVisible ? 'animate-in' : ''}`}>
              <div className="text-block">
                <h3>Our Mission</h3>
                <p>
                  At Oxgen Surgicals, we are committed to delivering premium medical instruments that meet global standards.
                  world-class surgical instruments that enable medical professionals to deliver 
                  exceptional patient care. Our mission is to combine traditional craftsmanship 
                  with modern technology to create instruments that meet the evolving needs of 
                  the medical community.
                </p>
              </div>
              
              <div className="text-block">
                <h3>Our Vision</h3>
                <p>
                  To be the global leader in surgical instrument innovation, setting new 
                  standards for quality, precision, and reliability in medical equipment. 
                  We envision a future where our instruments contribute to better surgical 
                  outcomes and improved patient safety worldwide.
                </p>
              </div>

              <div className="text-block">
                <h3>Quality Commitment</h3>
                <p>
                  Every instrument we manufacture undergoes stringent quality control processes. 
                  From material selection to final inspection, we maintain the highest standards 
                  to ensure that our products meet and exceed international medical device regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {isAboutRoute && (
          <>
            {/* Introduction */}
            <div className="reveal visible" style={{ maxWidth: 860, margin: '0 auto' }}>
              <h3 className="section-subtitle">Introduction</h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                Oxgen Surgicals designs and manufactures dependable surgical instruments for modern operating theatres. 
                Our approach blends craftsmanship with precision engineering, enabling clinicians to deliver exceptional patient care.
                We focus on durability, ergonomics, and process consistency—ensuring instruments perform reliably case after case.
              </p>
            </div>

            {/* Values */}
            <div
              className="reveal visible values-section"
              style={{
                marginTop: '2rem',
                backgroundImage: `url(${process.env.PUBLIC_URL}/value.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <h3 className="section-subtitle">Our Values</h3>
              <div className="values-grid">
                {values.map((v, i) => (
                  <div key={i} className="value-card">
                    <div className="value-icon">{v.icon}</div>
                    <div className="value-title">{v.title}</div>
                    <div className="value-text">{v.text}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Process */}
            <div
              className="reveal visible process-section"
              style={{
                marginTop: '2rem',
                backgroundImage: `url(${process.env.PUBLIC_URL}/work.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <h3 className="section-subtitle">How We Work</h3>
              <div className="process-grid">
                {processSteps.map((s, i) => (
                  <div key={i} className="process-card">
                    <div className="process-step">{s.step}</div>
                    <div className="process-title">{s.title}</div>
                    <div className="process-text">{s.text}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Journey */}
            <div
              className="reveal visible journey-section"
              style={{
                marginTop: '2rem',
                backgroundImage: `url(${process.env.PUBLIC_URL}/journey.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <h3 className="section-subtitle">Our Journey</h3>
              <div className="timeline">
                {timelineItems.map((t, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <span className="timeline-year">{t.year}</span>
                      <div className="timeline-event">{t.event}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="reveal visible" style={{ marginTop: '2rem' }}>
              <h3 className="section-subtitle">Quality & Compliance</h3>
              <div className="certifications-grid">
                {certifications.map((c, i) => (
                  <div key={i} className="cert-card">
                    <div className="cert-badge">{c.badge}</div>
                    <div className="cert-note">{c.note}</div>
                  </div>
                ))}
              </div>
              <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                These statements reflect our commitment to quality and process discipline.
              </p>
            </div>

            {/* Team */}
            <div
              className="reveal visible team-section"
              style={{
                marginTop: '2rem',
                backgroundImage: `url(${process.env.PUBLIC_URL}/leader.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <h3 className="section-subtitle">Leadership Team</h3>
              <div className="team-grid">
                {team.map((member, i) => (
                  <div key={i} className="team-card">
                    <div className="team-avatar">{member.initials}</div>
                    <div>
                      <div className="team-role">{member.role}</div>
                      <div className="team-text">{member.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Features */}
        <div className={`features-section ${isVisible ? 'animate-in' : ''}`}>
          <h3 className="features-title">Why Choose Oxgen Surgicals?</h3>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h4 className="feature-title">{feature.title}</h4>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`cta-section ${isVisible ? 'animate-in' : ''}`}>
          <div className="cta-content">
            <h3>Ready to Experience Excellence?</h3>
            <p>Join thousands of medical professionals who trust Oxgen Surgicals for their surgical instrument needs.</p>
            <div className="cta-actions">
              <a className="btn btn-primary btn-large" href="/#contact">Request Catalogue</a>
              <a className="btn btn-outline btn-large" href="/#contact">Schedule Consultation</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;